// Firma de los correos (v2.8). Graph no agrega la firma de Outlook: la aplicación la agrega al enviar. Aquí se ve
// cómo saldrá, se toma de Outlook, se pega o edita, o se vuelve a la automática.
import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ETIQUETA_ORIGEN_FIRMA, firmas, type FirmaCorreo } from '../api/firmas';
import { mensajeError } from '../hooks/useCasos';
import { fechaHora } from '../lib/formato';
import { useUi } from '../shell/contexto';
import { ErrorAccion, PanelError } from './Estados';
import { Icono } from './Iconos';
import { Dialogo } from './Ui';
import './firma-correo.css';

/** Vista previa aislada (sin scripts ni estilos que se filtren a la página). */
export function VistaFirma({ html, alto = 130 }: { html: string; alto?: number }) {
  const doc = `<!doctype html><html><head><meta charset="utf-8"><style>body{margin:8px;font-family:Calibri,Arial,sans-serif;font-size:10pt;color:#1f2937}img{max-width:100%}</style></head><body>${html}</body></html>`;
  return <iframe className="firma-vista" title="Vista previa de la firma" sandbox="" srcDoc={doc} style={{ height: alto }} />;
}

/** En el editor de un correo: la firma con la que saldrá. */
export function FirmaDelCorreo({ comunicacionId }: { comunicacionId: number }) {
  const q = useQuery({ queryKey: ['firma', 'comunicacion', comunicacionId], queryFn: () => firmas.deComunicacion(comunicacionId), retry: false });
  const [editar, setEditar] = useState(false);
  if (!q.data) return null;
  const f = q.data;
  return (
    <div className="firma-correo">
      <div className="firma-correo__cabecera">
        <span className="pequeno texto-sec">
          <Icono nombre="firma" tam={14} /> Firma que se agrega al enviar desde {f.buzon} · {ETIQUETA_ORIGEN_FIRMA[f.origen]}
        </span>
        {f.puede_editar && (
          <button type="button" className="btn btn--enlace btn--enlace-mini" onClick={() => setEditar(true)}>
            Cambiar firma
          </button>
        )}
      </div>
      <VistaFirma html={f.html} alto={110} />
      {editar && <DialogoFirmas alCerrar={() => (setEditar(false), void q.refetch())} soloBuzon={f.buzon} />}
    </div>
  );
}

/** Administración de las firmas que la persona puede cambiar (la suya y, para Preventa y Gerencia, la de Orion). */
export function DialogoFirmas({ alCerrar, soloBuzon }: { alCerrar: () => void; soloBuzon?: string }) {
  const q = useQuery({ queryKey: ['firmas'], queryFn: () => firmas.listar() });
  const items = (q.data?.items ?? []).filter((f) => !soloBuzon || f.buzon === soloBuzon.toLowerCase());
  return (
    <Dialogo abierto titulo="Firma de los correos" alCerrar={alCerrar} ancho={760}>
      <p className="pequeno texto-sec">
        Los correos que salen desde UPK INNOVA se envían por Microsoft Graph, que no agrega la firma de Outlook. La aplicación agrega esta firma al final de
        cada correo. Se toma sola de su último correo enviado desde Outlook (una vez al día); si Outlook no la marca, puede pegarla aquí.
      </p>
      {q.isError && <PanelError error={q.error} alReintentar={() => q.refetch()} />}
      {q.data && items.length === 0 && <p className="texto-tenue">Su sesión no tiene un buzón con firma para administrar.</p>}
      <div className="pila">
        {items.map((f) => (
          <TarjetaFirma key={f.buzon} f={f} />
        ))}
      </div>
    </Dialogo>
  );
}

function TarjetaFirma({ f }: { f: FirmaCorreo }) {
  const qc = useQueryClient();
  const ui = useUi();
  const [editando, setEditando] = useState(false);
  const editor = useRef<HTMLDivElement>(null);
  const refrescar = () => {
    void qc.invalidateQueries({ queryKey: ['firmas'] });
    void qc.invalidateQueries({ queryKey: ['firma'] });
  };
  const error = (e: unknown) => ui.avisar({ tono: 'error', ...mensajeError(e) });
  const guardar = useMutation({
    mutationFn: () => firmas.guardar(f.buzon, editor.current?.innerHTML ?? ''),
    onSuccess: () => {
      refrescar();
      setEditando(false);
      ui.avisar({ tono: 'exito', texto: 'Firma guardada' });
    },
  });
  const outlook = useMutation({
    mutationFn: () => firmas.desdeOutlook(f.buzon),
    onSuccess: (r) => {
      refrescar();
      ui.avisar(
        r.resultado === 'outlook'
          ? { tono: 'exito', texto: 'Firma tomada de Outlook' }
          : { tono: 'info', texto: 'Outlook no marcó la firma en los últimos correos', detalle: 'Se dejó la firma generada. Puede pegar la suya con «Editar».' },
      );
    },
    onError: error,
  });
  const restablecer = useMutation({ mutationFn: () => firmas.restablecer(f.buzon), onSuccess: refrescar, onError: error });
  return (
    <div className="firma-tarjeta">
      <div className="firma-tarjeta__cabecera">
        <div>
          <strong>{f.es_preventa ? `Buzón de preventa (${f.buzon})` : f.buzon}</strong>
          <span className="pequeno texto-tenue bloque">
            {ETIQUETA_ORIGEN_FIRMA[f.origen]}
            {f.actualizado ? ` · ${fechaHora(f.actualizado)}` : ''}
          </span>
        </div>
        {!editando && (
          <div className="botonera">
            <button type="button" className="btn btn--secundario btn--pequeno" onClick={() => setEditando(true)}>
              <Icono nombre="editar" /> Editar o pegar
            </button>
            <button type="button" className="btn btn--secundario btn--pequeno" onClick={() => outlook.mutate()} disabled={outlook.isPending}>
              <Icono nombre="buzon" /> {outlook.isPending ? 'Buscando…' : 'Tomar de Outlook'}
            </button>
            {f.origen === 'manual' && (
              <button type="button" className="btn btn--fantasma btn--pequeno" onClick={() => restablecer.mutate()} disabled={restablecer.isPending}>
                Volver a la automática
              </button>
            )}
          </div>
        )}
      </div>
      {editando ? (
        <div className="pila">
          <p className="pequeno texto-sec">Copie su firma desde un correo de Outlook y péguela aquí. Puede ajustar el texto; las imágenes pegadas (logo) se conservan.</p>
          <div
            ref={editor}
            className="firma-editor"
            contentEditable
            suppressContentEditableWarning
            role="textbox"
            aria-multiline="true"
            aria-label="Firma"
            dangerouslySetInnerHTML={{ __html: f.html }}
          />
          {guardar.isError && <ErrorAccion error={guardar.error} />}
          <div className="botonera">
            <button type="button" className="btn btn--primario btn--pequeno" onClick={() => guardar.mutate()} disabled={guardar.isPending}>
              {guardar.isPending ? 'Guardando…' : 'Guardar firma'}
            </button>
            <button type="button" className="btn btn--fantasma btn--pequeno" onClick={() => setEditando(false)}>
              Cancelar
            </button>
          </div>
        </div>
      ) : (
        <VistaFirma html={f.html} />
      )}
    </div>
  );
}
